import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createContact,
  fetchContactsStatus,
  listAccounts,
  listContactHistory,
  listContacts,
  oauthStart,
  syncContacts,
  type ContactItem,
} from '../lib/api'
import { resolveAccountAccent } from '../lib/accountAccent'
import { useUiStore } from '../lib/uiStore'
import { Icon } from './Icon'

type Props = {
  onBack: () => void
  onCompose: (to: string) => void
  onOpenMessage: (messageId: string, accountId?: string | null) => void
}

function sourceLabel(
  t: (k: string) => string,
  source: string,
  sources?: string[],
): string {
  const list = sources?.length ? sources : [source]
  return list
    .map((s) => {
      if (s === 'sync') return t('contacts.sourceSync')
      if (s === 'manual') return t('contacts.sourceManual')
      return t('contacts.sourceMail')
    })
    .join(' · ')
}

function looksLikeEmail(raw: string): boolean {
  const e = raw.trim().toLowerCase()
  return e.includes('@') && e.indexOf('@') > 0 && e.indexOf('@') < e.length - 1
}

export function ContactsPanel({ onBack, onCompose, onOpenMessage }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const accountPrefs = useUiStore((s) => s.accountPrefs)
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ContactItem | null>(null)
  const [manualEmail, setManualEmail] = useState('')
  const [manualName, setManualName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [reconnectAccountId, setReconnectAccountId] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['contacts-status'],
    queryFn: fetchContactsStatus,
    staleTime: 15_000,
  })
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    staleTime: 30_000,
  })
  const listQ = useQuery({
    queryKey: ['contacts', search],
    queryFn: () => listContacts({ q: search || undefined, limit: 300 }),
    staleTime: 10_000,
  })
  const histQ = useQuery({
    queryKey: ['contacts-history', selected?.email],
    queryFn: () => listContactHistory(selected!.email, 40),
    enabled: !!selected?.email,
  })

  const oauthAccounts = useMemo(
    () => statusQ.data?.accounts || [],
    [statusQ.data],
  )
  const mailAccounts = accountsQ.data?.items || []
  const connectedCount = oauthAccounts.filter((a) => a.contacts_connected).length

  const accountColor = useMemo(() => {
    const m = new Map<string, string>()
    mailAccounts.forEach((a, idx) => {
      m.set(
        a.id,
        resolveAccountAccent({
          accountId: a.id,
          provider: a.provider,
          index: idx,
          prefColor: accountPrefs[a.id]?.color,
        }),
      )
    })
    oauthAccounts.forEach((a, idx) => {
      if (!m.has(a.account_id)) {
        m.set(
          a.account_id,
          resolveAccountAccent({
            accountId: a.account_id,
            provider: a.provider,
            index: idx,
            prefColor: accountPrefs[a.account_id]?.color,
          }),
        )
      }
    })
    return m
  }, [mailAccounts, oauthAccounts, accountPrefs])

  function colorsForContact(c: ContactItem): string[] {
    const ids = c.account_ids?.length
      ? c.account_ids
      : c.account_id
        ? [c.account_id]
        : []
    const colors = ids
      .map((id) => accountColor.get(id))
      .filter((x): x is string => !!x)
    if (colors.length) return [...new Set(colors)].slice(0, 3)
    if (c.has_manual) return ['#6d28d9']
    if (c.has_mail) return ['#64748b']
    return ['#94a3b8']
  }

  function userFacingSyncError(
    code: string | null | undefined,
    needsReconnect?: boolean,
  ): string {
    if (code === 'contacts_scope_missing' || code === 'contacts_permission_denied') {
      return t('contacts.reconnectNeeded')
    }
    if (needsReconnect || code) {
      return t('contacts.syncUnavailable')
    }
    return t('contacts.syncUnavailable')
  }

  useEffect(() => {
    if (!note && !err) return
    const id = window.setTimeout(() => {
      setNote(null)
      setErr(null)
      setReconnectAccountId(null)
    }, 8000)
    return () => window.clearTimeout(id)
  }, [note, err])

  async function connectContacts(accountId: string, provider: string) {
    setBusy(true)
    setErr(null)
    try {
      const p = (provider || '').toLowerCase()
      const oauthProvider =
        p.includes('gmail') || p.includes('google') ? 'gmail' : 'microsoft'
      const res = await oauthStart(oauthProvider, {
        purpose: 'contacts',
        account_id: accountId,
        write: false,
      })
      window.location.href = res.authorize_url
    } catch {
      setErr(t('contacts.connectFailed'))
      setBusy(false)
    }
  }

  async function runSync(accountId?: string) {
    setBusy(true)
    setErr(null)
    setNote(null)
    setReconnectAccountId(null)
    try {
      const res = await syncContacts(accountId)
      if (!res.ok) {
        setErr(userFacingSyncError(res.error, res.needs_reconnect))
        if (res.needs_reconnect && accountId) {
          setReconnectAccountId(accountId)
        } else if (res.needs_reconnect && oauthAccounts[0]) {
          setReconnectAccountId(oauthAccounts[0].account_id)
        }
        if ((res.mail_derived ?? 0) > 0) {
          setNote(
            t('contacts.syncedPartial', {
              mail: res.mail_derived ?? 0,
            }),
          )
        }
      } else {
        setNote(
          t('contacts.syncedOk', {
            sync: res.contacts ?? 0,
            mail: res.mail_derived ?? 0,
          }),
        )
      }
      await qc.invalidateQueries({ queryKey: ['contacts'] })
      await qc.invalidateQueries({ queryKey: ['contacts-status'] })
    } catch {
      setErr(t('contacts.syncUnavailable'))
    } finally {
      setBusy(false)
    }
  }

  async function addManual() {
    setBusy(true)
    setErr(null)
    setNote(null)
    const email = manualEmail.trim()
    const name = manualName.trim()
    if (!looksLikeEmail(email)) {
      setErr(t('contacts.invalidEmail'))
      setBusy(false)
      return
    }
    try {
      const res = await createContact({
        email,
        display_name: name || undefined,
      })
      const item: ContactItem = {
        id: res.email,
        email: res.email,
        display_name: res.display_name ?? (name || null),
        sources: res.sources || ['manual'],
        source: 'manual',
        message_count: 0,
        last_contact_at: null,
        has_sync: false,
        has_manual: true,
        has_mail: false,
        account_ids: [],
        account_id: null,
      }
      setManualEmail('')
      setManualName('')
      setQ(res.email)
      setSearch(res.email)
      setSelected(item)
      setNote(t('contacts.manualSavedOk', { email: res.email }))
      await qc.invalidateQueries({ queryKey: ['contacts'] })
    } catch {
      setErr(t('contacts.manualFailed'))
    } finally {
      setBusy(false)
    }
  }

  const reconnectRow = reconnectAccountId
    ? oauthAccounts.find((a) => a.account_id === reconnectAccountId)
    : null

  return (
    <div className="rd-contacts">
      <div className="rd-panel-head">
        <button type="button" className="rd-back" onClick={onBack} aria-label={t('auth.cancel')}>
          <Icon name="arrow_back" size={20} />
        </button>
        <div>
          <h2>{t('mail.contacts')}</h2>
          <p className="d">{t('contacts.lead')}</p>
        </div>
      </div>

      <div className="rd-contacts-body rd-sec-body">
        <div className="rd-contacts-toolbar">
          <form
            className="rd-contacts-search"
            onSubmit={(e) => {
              e.preventDefault()
              setSearch(q.trim())
            }}
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('contacts.searchPlaceholder')}
              aria-label={t('contacts.searchPlaceholder')}
            />
            <button type="submit" className="rd-rbtn2">
              {t('contacts.search')}
            </button>
          </form>
          <button
            type="button"
            className="rd-rbtn2"
            disabled={busy}
            onClick={() => void runSync()}
          >
            {t('contacts.refreshMail')}
          </button>
        </div>

        {oauthAccounts.length > 0 ? (
          <div className="rd-cal-legend rd-contacts-legend" role="group" aria-label={t('contacts.legend')}>
            {oauthAccounts.map((a, idx) => {
              const color =
                accountColor.get(a.account_id) ||
                resolveAccountAccent({
                  accountId: a.account_id,
                  provider: a.provider,
                  index: idx,
                })
              return (
                <span key={a.account_id} className="rd-cal-leg">
                  <span className="swatch" style={{ background: color }} />
                  <span className="lab">
                    {a.label || a.username}
                    {a.contacts_connected ? '' : ` · ${t('contacts.notConnected')}`}
                  </span>
                </span>
              )
            })}
            <span className="rd-cal-leg">
              <span className="swatch" style={{ background: '#6d28d9' }} />
              <span className="lab">{t('contacts.sourceManual')}</span>
            </span>
            <span className="rd-cal-leg">
              <span className="swatch" style={{ background: '#64748b' }} />
              <span className="lab">{t('contacts.sourceMail')}</span>
            </span>
          </div>
        ) : null}

        <details className="rd-cal-connect rd-contacts-connect" open={!!reconnectAccountId}>
          <summary className="rd-cal-connect-sum">
            <span>
              {t('contacts.connectTitle')}
              {connectedCount > 0
                ? ` · ${t('contacts.connectedCount', { count: connectedCount })}`
                : ''}
            </span>
            <Icon name="expand_more" size={20} />
          </summary>
          <div className="rd-cal-connect-body">
            <p className="d">{t('contacts.connectHint')}</p>
            {oauthAccounts.length === 0 ? (
              <p className="d">{t('contacts.noOauthAccounts')}</p>
            ) : (
              <ul className="rd-cal-accts">
                {oauthAccounts.map((a) => {
                  const color = accountColor.get(a.account_id)
                  return (
                    <li key={a.account_id}>
                      <div>
                        <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {color ? (
                            <span
                              className="rd-contacts-dot"
                              style={{ background: color }}
                              aria-hidden
                            />
                          ) : null}
                          {a.label || a.username}
                        </strong>
                        <div className="d">{a.username}</div>
                        {a.contacts_connected ? (
                          <em className="rd-cal-ok">{t('contacts.connected')}</em>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {a.contacts_connected ? (
                          <>
                            <button
                              type="button"
                              className="rd-rbtn2"
                              disabled={busy}
                              onClick={() => void runSync(a.account_id)}
                            >
                              {t('contacts.syncNow')}
                            </button>
                            <button
                              type="button"
                              className="rd-rbtn2"
                              disabled={busy}
                              onClick={() => void connectContacts(a.account_id, a.provider)}
                            >
                              {t('contacts.reconnect')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="rd-rbtn2"
                            disabled={busy}
                            onClick={() => void connectContacts(a.account_id, a.provider)}
                          >
                            {t('mail.connectContacts')}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </details>

        <div className="rd-contacts-manual">
          <h3>{t('contacts.addManual')}</h3>
          <input
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder={t('contacts.namePlaceholder')}
            autoComplete="name"
          />
          <input
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            placeholder={t('contacts.emailPlaceholder')}
            autoComplete="email"
            inputMode="email"
          />
          <button
            type="button"
            className="rd-rbtn2"
            disabled={busy || !manualEmail.trim()}
            onClick={() => void addManual()}
          >
            {t('contacts.saveManual')}
          </button>
        </div>

        {err ? (
          <p className="d rd-cal-note rd-contacts-flash warn" role="alert">
            {err}
            {reconnectRow ? (
              <>
                {' '}
                <button
                  type="button"
                  className="rd-rbtn2"
                  disabled={busy}
                  onClick={() =>
                    void connectContacts(reconnectRow.account_id, reconnectRow.provider)
                  }
                >
                  {t('contacts.reconnect')}
                </button>
              </>
            ) : null}
          </p>
        ) : null}
        {note ? (
          <p className="d rd-cal-note rd-contacts-flash ok" role="status">
            {note}
          </p>
        ) : null}

        <div className="rd-contacts-main">
          <ul className="rd-contacts-list">
            {(listQ.data?.items || []).map((c) => {
              const colors = colorsForContact(c)
              return (
                <li key={c.email}>
                  <button
                    type="button"
                    className={`rd-contacts-row${selected?.email === c.email ? ' sel' : ''}`}
                    onClick={() => setSelected(c)}
                    style={
                      {
                        '--acct-accent': colors[0],
                      } as CSSProperties
                    }
                  >
                    <span className="rd-contacts-accents" aria-hidden>
                      {colors.map((col) => (
                        <span key={col} className="rd-contacts-accent" style={{ background: col }} />
                      ))}
                    </span>
                    <span className="rd-contacts-row-body">
                      <span className="nm">{c.display_name || c.email}</span>
                      {c.display_name ? <span className="em">{c.email}</span> : null}
                      <span className="src">{sourceLabel(t, c.source, c.sources)}</span>
                    </span>
                  </button>
                </li>
              )
            })}
            {!listQ.isLoading && (listQ.data?.items || []).length === 0 ? (
              <li className="d">{t('contacts.empty')}</li>
            ) : null}
          </ul>

          <div className="rd-contacts-detail">
            {selected ? (
              <>
                <h3>{selected.display_name || selected.email}</h3>
                <p className="d">{selected.email}</p>
                <p className="d">{sourceLabel(t, selected.source, selected.sources)}</p>
                <div className="rd-contacts-actions">
                  <button
                    type="button"
                    className="rd-compose"
                    style={{ margin: 0 }}
                    onClick={() => onCompose(selected.email)}
                  >
                    {t('contacts.compose')}
                  </button>
                </div>
                <h4>{t('contacts.history')}</h4>
                {histQ.isLoading ? <p className="d">…</p> : null}
                <ul className="rd-contacts-hist">
                  {(histQ.data?.items || []).map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        className="rd-contacts-hist-row"
                        onClick={() => onOpenMessage(m.id, m.account_id)}
                      >
                        <span className="subj">{m.subject || t('contacts.noSubject')}</span>
                        <span className="meta">
                          {m.direction} · {m.date_sent ? new Date(m.date_sent).toLocaleString() : '—'}
                        </span>
                      </button>
                    </li>
                  ))}
                  {!histQ.isLoading && (histQ.data?.items || []).length === 0 ? (
                    <li className="d">{t('contacts.historyEmpty')}</li>
                  ) : null}
                </ul>
              </>
            ) : (
              <p className="d">{t('contacts.pickHint')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
